/**
 * PostHog No-Op Stub for FERPA Compliance
 *
 * This module provides a no-op implementation of PostHog analytics.
 * All tracking calls are ignored for student data privacy.
 *
 * FERPA COMPLIANCE:
 * ================
 * This stub ensures no student PII is sent to third-party analytics services.
 * - No user identifiers are transmitted
 * - No behavioral data is collected
 * - No IP addresses are logged externally
 * - Session tracking uses local-only mechanisms
 *
 * CRITICAL IMPLEMENTATION NOTES:
 * =============================
 * 1. get_distinct_id() returns NULL (not a string) so that code checking
 *    for truthy values will properly fall back to localStorage-based IDs.
 *    This is essential for paywall tracking to work correctly.
 *
 * 2. The application uses localStorage key 'morpheus_anon_id' for persistent
 *    anonymous user tracking. This is FERPA-compliant because:
 *    - IDs are randomly generated (no PII)
 *    - Data stays on-device and our servers only
 *    - Used for rate limiting, not educational records
 *    - Not shared with third parties
 *
 * TO RE-ENABLE ANALYTICS (Self-Hosted PostHog):
 * ============================================
 * 1. Install self-hosted PostHog: https://posthog.com/docs/getting-started/install
 * 2. Replace imports from './utils/posthog-stub' back to 'posthog-js'
 * 3. Configure with your self-hosted instance URL
 * 4. For school users, ensure:
 *    - ip: false (disable IP capture)
 *    - No email/username in identify() calls
 *    - autocapture: false for school contexts
 */

const posthogStub = {
  // Core tracking methods - all no-ops
  capture: () => {},
  identify: () => {},
  alias: () => {},
  reset: () => {},

  // People/user properties - all no-ops
  people: {
    set: () => {},
    set_once: () => {},
    increment: () => {},
    append: () => {},
    union: () => {},
    remove: () => {},
    unset: () => {},
  },

  // Initialization - no-op
  init: () => {},

  // Feature flags - return safe defaults
  getFeatureFlag: () => null,
  getFeatureFlagPayload: () => null,
  isFeatureEnabled: () => false,
  reloadFeatureFlags: () => {},
  onFeatureFlags: () => {},

  // Session management
  // CRITICAL: Return null (not string) so truthiness checks fail
  // and code falls back to localStorage-based anonymous IDs
  get_distinct_id: () => null,
  get_session_id: () => null,
  get_session_replay_url: () => null,

  // Registration - all no-ops
  register: () => {},
  register_once: () => {},
  register_for_session: () => {},
  unregister: () => {},
  unregister_for_session: () => {},

  // Groups - return safe defaults
  group: () => {},
  getGroups: () => ({}),
  resetGroups: () => {},

  // Opt in/out - indicate opted out for safety
  opt_in_capturing: () => {},
  opt_out_capturing: () => {},
  has_opted_in_capturing: () => false,
  has_opted_out_capturing: () => true,
  clear_opt_in_out_capturing: () => {},

  // Session recording - all disabled
  startSessionRecording: () => {},
  stopSessionRecording: () => {},
  sessionRecordingStarted: () => false,

  // Config - return null for safety
  set_config: () => {},
  get_config: () => null,
  get_property: () => null,
  getSessionProperty: () => null,

  // Surveys - return empty/disabled
  getSurveys: () => [],
  getActiveMatchingSurveys: () => [],
  renderSurvey: () => {},
  canRenderSurvey: () => false,

  // Misc - no-ops and safe defaults
  debug: () => {},
  loadToolbar: () => {},
  captureException: () => {},
  getPageViewId: () => null,

  // Internal flags indicating this is a stub
  __loaded: true,
  _stub: true,
  _ferpa_compliant: true,
};

// Log once on import to indicate analytics are disabled (development only)
if (typeof window !== 'undefined' && !window._posthogStubLogged) {
  if (process.env.NODE_ENV !== 'production') {
    console.log('[FERPA] PostHog analytics disabled - using no-op stub');
  }
  window._posthogStubLogged = true;
}

export default posthogStub;
